import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  TextField,
  IconButton,
  Stack,
  Divider,
  Alert,
  Switch,
  FormControlLabel,
  Checkbox,
  Collapse,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";

const HirePurchaseSettingsDialog = ({
  open,
  onClose,
  globalDeferredYears = [],
  onSave,
  yearOfAssessment,
  existingAssets = [],
  codePrefix: initialCodePrefix = "HPA",
  assetCodeList: initialAssetCodeList = [],
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [deferredYears, setDeferredYears] = useState([]);
  const [newYear, setNewYear] = useState("");
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [selectedYearsToApply, setSelectedYearsToApply] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Asset Code Management State
  const [codePrefix, setCodePrefix] = useState("HPA");
  const [assetCodeList, setAssetCodeList] = useState([]);
  const [editingCodeId, setEditingCodeId] = useState(null);
  const [editingCodeValue, setEditingCodeValue] = useState("");

  useEffect(() => {
    if (open && !isInitialized) {
      // Only initialize when dialog first opens
      setDeferredYears([...globalDeferredYears]);
      setNewYear("");
      setApplyToExisting(false);
      setSelectedYearsToApply([]);
      setCodePrefix(initialCodePrefix);
      setAssetCodeList(initialAssetCodeList);
      setIsInitialized(true);
    } else if (!open) {
      // Reset initialization flag when dialog closes
      setIsInitialized(false);
      setActiveTab(0);
      setEditingCodeId(null);
    }
  }, [
    open,
    globalDeferredYears,
    isInitialized,
    initialCodePrefix,
    initialAssetCodeList,
  ]);

  const handleAddYear = () => {
    const year = parseInt(newYear);
    if (year && !deferredYears.includes(year)) {
      setDeferredYears([...deferredYears, year].sort((a, b) => a - b));
      setNewYear("");
    }
  };

  const handleRemoveYear = (yearToRemove) => {
    setDeferredYears(deferredYears.filter((year) => year !== yearToRemove));
  };

  const handleSave = () => {
    onSave(
      deferredYears,
      applyToExisting ? selectedYearsToApply : [],
      codePrefix,
      assetCodeList
    );
    onClose();
  };

  const handleClose = () => {
    setDeferredYears([...globalDeferredYears]);
    setNewYear("");
    setApplyToExisting(false);
    setSelectedYearsToApply([]);
    setCodePrefix(initialCodePrefix);
    setAssetCodeList([...initialAssetCodeList]);
    setEditingCodeId(null);
    onClose();
  };

  // Asset Code Management Functions
  const handleStartEditCode = (assetId, currentCode) => {
    setEditingCodeId(assetId);
    setEditingCodeValue(currentCode || "");
  };

  const handleCancelEditCode = () => {
    setEditingCodeId(null);
    setEditingCodeValue("");
  };

  const handleSaveCode = (assetId) => {
    const trimmedCode = editingCodeValue.trim();

    // Validate: check for duplicates
    const isDuplicate = assetCodeList.some(
      (item) => item.assetCode === trimmedCode && item.assetId !== assetId
    );

    if (isDuplicate) {
      alert(
        "This asset code is already in use. Please choose a different code."
      );
      return;
    }

    // Update or add asset code
    const existingIndex = assetCodeList.findIndex(
      (item) => item.assetId === assetId
    );

    if (existingIndex >= 0) {
      // Update existing
      const updated = [...assetCodeList];
      updated[existingIndex] = { assetId, assetCode: trimmedCode };
      setAssetCodeList(updated);
    } else {
      // Add new
      setAssetCodeList([...assetCodeList, { assetId, assetCode: trimmedCode }]);
    }

    setEditingCodeId(null);
    setEditingCodeValue("");
  };

  const handleRegenerateCodes = () => {
    if (
      !window.confirm(
        "This will regenerate asset codes for all assets without existing codes. Continue?"
      )
    ) {
      return;
    }

    const usedCodes = new Set(
      assetCodeList.map((a) => a.assetCode).filter(Boolean)
    );
    const updatedList = [...assetCodeList];

    existingAssets.forEach((asset, index) => {
      const existing = updatedList.find((a) => a.assetId === asset.assetId);

      if (!existing || !existing.assetCode) {
        // Generate new code
        let code = `${codePrefix}${String(index + 1).padStart(3, "0")}`;
        while (usedCodes.has(code)) {
          code = `${codePrefix}${String(
            Math.floor(Math.random() * 100000)
          ).padStart(5, "0")}`;
        }
        usedCodes.add(code);

        if (existing) {
          existing.assetCode = code;
        } else {
          updatedList.push({ assetId: asset.assetId, assetCode: code });
        }
      }
    });

    setAssetCodeList(updatedList);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <SettingsIcon color='primary' />
            <Typography variant='h6' fontWeight={600}>
              Hire Purchase Settings
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label='Deferred Years' />
          <Tab label='Asset Codes' />
        </Tabs>

        {activeTab === 0 && (
          <Stack spacing={3}>
            <Alert severity='info' sx={{ borderRadius: 2 }}>
              <Typography variant='body2'>
                Set default deferred years that will be applied to all assets.
                Deferred years are periods where capital allowance claims are
                postponed.
              </Typography>
            </Alert>

            <Box>
              <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                Default Deferred Years
              </Typography>

              <Box display='flex' gap={1} alignItems='center' sx={{ mb: 2 }}>
                <TextField
                  size='small'
                  placeholder='Enter year (e.g., 2023)'
                  value={newYear}
                  onChange={(e) => setNewYear(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddYear();
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleAddYear}
                  disabled={
                    !newYear || deferredYears.includes(parseInt(newYear))
                  }
                  size='small'
                >
                  Add
                </Button>
              </Box>

              {deferredYears.length > 0 ? (
                <Box>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    Selected deferred years:
                  </Typography>
                  <Box display='flex' flexWrap='wrap' gap={1}>
                    {deferredYears.map((year) => (
                      <Chip
                        key={year}
                        label={year}
                        onDelete={() => handleRemoveYear(year)}
                        deleteIcon={<DeleteIcon />}
                        color='primary'
                        variant='outlined'
                        sx={{
                          "& .MuiChip-deleteIcon": {
                            color: "error.main",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    No deferred years set. All assets will use their individual
                    deferred year settings.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Apply to Existing Assets Section */}
            {existingAssets.length > 0 && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={applyToExisting}
                      onChange={(e) => setApplyToExisting(e.target.checked)}
                      color='primary'
                      sx={{ ml: 1.5 }}
                    />
                  }
                  label={
                    <Typography
                      variant='subtitle1'
                      fontWeight={600}
                      sx={{ ml: 2 }}
                    >
                      Apply Deferred Years to All Existing Assets
                    </Typography>
                  }
                />

                <Collapse in={applyToExisting}>
                  <Box sx={{ mt: 2, pl: 4 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      Select which deferred years to add to existing assets:
                    </Typography>

                    {deferredYears.length > 0 ? (
                      <Box>
                        {deferredYears.map((year) => {
                          const isSelected =
                            selectedYearsToApply.includes(year);
                          const isNewYear = !globalDeferredYears.includes(year);

                          return (
                            <FormControlLabel
                              key={year}
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedYearsToApply([
                                        ...selectedYearsToApply,
                                        year,
                                      ]);
                                    } else {
                                      setSelectedYearsToApply(
                                        selectedYearsToApply.filter(
                                          (y) => y !== year
                                        )
                                      );
                                    }
                                  }}
                                  color='primary'
                                />
                              }
                              label={
                                <Box display='flex' alignItems='center' gap={1}>
                                  <Typography variant='body2'>
                                    {year}
                                  </Typography>
                                  {isNewYear && (
                                    <Chip
                                      label='New'
                                      size='small'
                                      color='success'
                                      variant='outlined'
                                      sx={{ fontSize: "0.7rem", height: 20 }}
                                    />
                                  )}
                                  {!isNewYear && (
                                    <Chip
                                      label='Existing'
                                      size='small'
                                      color='warning'
                                      variant='outlined'
                                      sx={{ fontSize: "0.7rem", height: 20 }}
                                    />
                                  )}
                                </Box>
                              }
                              sx={{
                                mb: 1,
                              }}
                            />
                          );
                        })}

                        {selectedYearsToApply.length > 0 && (
                          <Alert
                            severity='info'
                            sx={{ mt: 2, borderRadius: 2 }}
                          >
                            <Typography variant='body2'>
                              {selectedYearsToApply.length} deferred year
                              {selectedYearsToApply.length > 1 ? "s" : ""} will
                              be added to {existingAssets.length} existing asset
                              {existingAssets.length > 1 ? "s" : ""}.
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No deferred years set yet. Add some years above first.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography variant='subtitle2' fontWeight={600} gutterBottom>
                How it works:
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                • These deferred years will be applied as defaults to new assets
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                • Existing assets will keep their individual settings
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                • You can selectively add any deferred years to existing assets
                using the option above
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                • You can still override these defaults for individual assets
              </Typography>
            </Box>
          </Stack>
        )}

        {activeTab === 1 && (
          <Stack spacing={3}>
            <Alert severity='info' sx={{ borderRadius: 2 }}>
              <Typography variant='body2'>
                Manage asset codes for your hire purchase assets. Asset codes
                are used to identify and link assets across different schedules.
              </Typography>
            </Alert>

            <Box>
              <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                Code Prefix
              </Typography>
              <TextField
                size='small'
                label='Asset Code Prefix'
                value={codePrefix}
                onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
                helperText='Prefix used when generating new asset codes (e.g., HPA, HPB)'
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 10 }}
              />
            </Box>

            <Divider />

            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant='subtitle1' fontWeight={600}>
                  Asset Codes
                </Typography>
                {existingAssets.length > 0 && (
                  <Tooltip title='Regenerate codes for assets without codes'>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<RefreshIcon />}
                      onClick={handleRegenerateCodes}
                    >
                      Regenerate Missing Codes
                    </Button>
                  </Tooltip>
                )}
              </Box>

              {existingAssets.length > 0 ? (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                >
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.03),
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            borderBottom: "2px solid",
                            borderColor: (theme) =>
                              alpha(theme.palette.divider, 0.1),
                            py: 1.5,
                          }}
                        >
                          Asset Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            borderBottom: "2px solid",
                            borderColor: (theme) =>
                              alpha(theme.palette.divider, 0.1),
                            py: 1.5,
                          }}
                        >
                          Asset Code
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            borderBottom: "2px solid",
                            borderColor: (theme) =>
                              alpha(theme.palette.divider, 0.1),
                            py: 1.5,
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {existingAssets.map((asset) => {
                        const codeEntry = assetCodeList.find(
                          (a) => a.assetId === asset.assetId
                        );
                        const currentCode = codeEntry?.assetCode || "";
                        const isEditing = editingCodeId === asset.assetId;

                        return (
                          <TableRow key={asset.assetId}>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: "text.primary",
                                py: 1.5,
                              }}
                            >
                              {asset.assetName}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              {isEditing ? (
                                <TextField
                                  size='small'
                                  value={editingCodeValue}
                                  onChange={(e) =>
                                    setEditingCodeValue(
                                      e.target.value.toUpperCase()
                                    )
                                  }
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveCode(asset.assetId);
                                    } else if (e.key === "Escape") {
                                      handleCancelEditCode();
                                    }
                                  }}
                                  autoFocus
                                  inputProps={{ maxLength: 20 }}
                                  sx={{ width: 150 }}
                                />
                              ) : (
                                <Typography
                                  variant='body2'
                                  sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 500,
                                    color: currentCode
                                      ? "text.primary"
                                      : "text.secondary",
                                  }}
                                >
                                  {currentCode || "Not assigned"}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align='right' sx={{ py: 1.5 }}>
                              {isEditing ? (
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <IconButton
                                    size='small'
                                    color='success'
                                    onClick={() =>
                                      handleSaveCode(asset.assetId)
                                    }
                                  >
                                    <CheckIcon fontSize='small' />
                                  </IconButton>
                                  <IconButton
                                    size='small'
                                    color='error'
                                    onClick={handleCancelEditCode}
                                  >
                                    <CancelIcon fontSize='small' />
                                  </IconButton>
                                </Box>
                              ) : (
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    handleStartEditCode(
                                      asset.assetId,
                                      currentCode
                                    )
                                  }
                                >
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    p: 3,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    No assets available. Add assets first to manage their codes.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant='subtitle2' fontWeight={600} gutterBottom>
                How it works:
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                • Asset codes are unique identifiers for each asset
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                • New assets will automatically get codes based on the prefix
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                • You can manually edit individual asset codes
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                • Changing the prefix only affects newly generated codes
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} color='inherit'>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={
            activeTab === 0 &&
            JSON.stringify(deferredYears) ===
              JSON.stringify(globalDeferredYears) &&
            selectedYearsToApply.length === 0
          }
        >
          {activeTab === 0 && applyToExisting && selectedYearsToApply.length > 0
            ? `Save & Apply to ${existingAssets.length} Asset${
                existingAssets.length > 1 ? "s" : ""
              }`
            : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HirePurchaseSettingsDialog;
